import React, { useState, useRef, useEffect } from "react";
import FreeToolsLayout from "./FreeToolsLayout";
import sparkleWhite from "~/images/sparkle-white.svg";
import sparklePurple from "~/images/sparkle-purple.svg";
import { generateTopicalMap } from "../../api/freeToolsApis";
import GenerateResults from "../shared/GenerateResults";
const TopicalMapGenerator = () => {
  const [mainTopic, setMainTopic] = useState("");
  const [subtopics, setSubtopics] = useState("");
  const [existingContent, setExistingContent] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cluster, setCluster] = useState(null);
  const resultRef = useRef(null);
  const handleGenerate = async () => {
    setError("");
    setCluster(null);

    if (!mainTopic.trim()) {
      setError("Please enter a main topic");
      return;
    }

    setLoading(true);

    try {
      const response = await generateTopicalMap({ main_topic: mainTopic, subtopics, existing_content: existingContent });
      if (response.status === 200) {
        setCluster(response.data.topic_cluster);
      } else {
        throw new Error(response.data.error || "Failed to generate topical map");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const getButtonText = () => {
    if (loading) return "Generating...";
    if (cluster) return "Generate Again";
    return "Generate";
  };
  useEffect(() => {
    if (cluster && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [cluster]);
  return (
    <FreeToolsLayout mainHeading="AI SEO Topical Map Generator" subHeading="Create comprehensive topical maps to establish content authority and plan your SEO strategy using AI">
      <div className="bg-white/70 rounded-[20px] py-8 px-[26px] shadow-md shadow-[#6248fe1f] relative min-h-[200px]">
        <div className="mb-4">
          <label className="text-[#3f3f3f] text-base font-bold mb-1">Main Topic</label>
          <p className="text-[#696969] text-sm mb-2">The primary topic you want to establish authority for</p>
          <input
            type="text"
            class="w-full bg-transparent rounded-lg border-[#e9e4ff] outline-none focus:border-[#e9e4ff] text-black placeholder-[#707070] focus:ring-0 text-sm"
            placeholder="e.g., Content Marketing"
            value={mainTopic}
            onChange={(e) => setMainTopic(e.target.value)}
          />
        </div>
        <div className="mb-4">
          <label className="text-[#3f3f3f] text-base font-bold mb-1">Known Subtopics (Optional)</label>
          <p className="text-[#696969] text-sm mb-2">List any subtopics you already know about (one per line)</p>
          <div class="mt-2">
            <textarea
              placeholder="Enter any known subtopics (one per line)..."
              value={subtopics}
              onChange={(e) => setSubtopics(e.target.value)}
              id="inputText"
              className="w-full pr-16 bg-transparent rounded-lg border-[#e9e4ff] outline-none focus:border-[#e9e4ff] resize-none text-sm min-h-[120px] text-black placeholder-[#707070] focus:ring-0"
            ></textarea>
          </div>
        </div>

        <div className="mb-4">
          <label className="text-[#3f3f3f] text-base font-bold mb-1">Existing Content URLs (Optional)</label>
          <p className="text-[#696969] text-sm mb-2">List URLs of your existing content to identify gaps and opportunities</p>
          <div class="mt-2">
            <textarea
              placeholder="Enter URLs of your existing content (one per line)..."
              value={existingContent}
              onChange={(e) => setExistingContent(e.target.value)}
              id="inputText"
              className="w-full pr-16 bg-transparent rounded-lg border-[#e9e4ff] outline-none focus:border-[#e9e4ff] resize-none text-sm min-h-[120px] text-black placeholder-[#707070] focus:ring-0"
            ></textarea>
          </div>
        </div>
        <GenerateResults loading={loading} handleGenerate={handleGenerate} text={getButtonText()} />
      </div>
      {error && <div className="text-red-600 text-sm px-2">{error}</div>}
      {cluster && (
        <div ref={resultRef}>
          <div className="flex items-center justify-center mb-4">
            <img src={sparklePurple} />
          </div>
          <div class="bg-white/70 rounded-[20px] p-[26px] shadow-md shadow-[#6248fe1f] relative flex flex-col gap-5">
            <div class="border border-[#dfdaff] rounded-[10px] p-5">
              <div class="text-[20px] mb-4 font-semibold">Core Concepts</div>
              <div class="flex gap-3 flex-wrap">
                {cluster.core_concepts.map((concept, i) => (
                  <span key={i} class="bg-[#6248fe1a] font-semibold text-[14px] text-[#6248FE] p-1.5 border border-[#6248fe61] rounded-[5px]">
                    {concept}
                  </span>
                ))}
              </div>
            </div>
            {cluster.subtopics.map((sub, i) => (
              <div key={i} class="border border-[#dfdaff] rounded-[10px] p-5">
                <div class="text-[20px] mb-2.5 font-semibold">{sub.name}</div>
                <div class="text-[15px] mb-3 font-semibold">Related Terms:</div>
                <div class="flex gap-3 flex-wrap mb-4">
                  {sub.related_terms.map((term, idx) => (
                    <span key={idx} class="bg-[#6248fe1a] font-semibold text-[14px] text-[#6248FE] p-1.5 border border-[#6248fe61] rounded-[5px]">
                      {term}
                    </span>
                  ))}
                </div>
                <div class="text-[15px] mb-3 font-semibold">Key Questions to Answer:</div>
                <ul class="text-[15px] space-y-1 ml-4 list-disc">
                  {sub.key_questions.map((q, idx) => (
                    <li key={idx}>{q}</li>
                  ))}
                </ul>

                <div class="text-[15px] mb-4 font-semibold mt-5">Content Suggestions</div>
                <div class="space-y-4 mb-6">
                  {sub.content_suggestions.map((content, idx) => (
                    <div key={idx} class="bg-[#6248fe0f] p-4 rounded-[8px] rounded-l-none border-l-4 border-[#6248fe]">
                      <div class="text-[15px] font-semibold mb-2">{content.title}</div>
                      <div class="text-[14px] text-[#696969] mb-4">{content.description}</div>
                      <div class="flex gap-3 flex-wrap mb-4">
                        {content.target_keywords.map((kw, kdx) => (
                          <span key={kdx} class="bg-[#6248fe1a] font-semibold text-[11px] text-[#6248FE] py-1 px-1.5 border border-[#6248fe61] rounded-[5px]">
                            {kw}
                          </span>
                        ))}
                      </div>
                      <div class="flex justify-between text-[15px]">
                        <span>Type: {content.content_type}</span>
                        <span>~{content.estimated_word_count} words</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div class="border border-[#dfdaff] bg-[#6248fe0f] rounded-[10px] p-5">
              <div class="text-[20px] mb-2.5 font-semibold">Content Gaps to Address</div>
              <ul class="text-[15px] space-y-1 ml-4 list-disc">
                {cluster.content_gaps.map((gap, i) => (
                  <li key={i}>{gap}</li>
                ))}
              </ul>
            </div>
            <div class="border border-[#dfdaff] rounded-[10px] p-5">
              <div class="text-[20px] mb-4 font-semibold">Internal Linking Suggestions</div>
              <div class="space-y-4">
                {cluster.internal_linking_suggestions.map((link, i) => (
                  <div key={i} class="flex flex-col gap-3 p-5 bg-[#6248fe0f] rounded-[8px] border border-[#e5e7eb]">
                    <div class="flex items-center gap-4 justify-between">
                      <span class="text-[15px] font-medium">{link.from}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6248fe" stroke-width="2">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                      <span class="text-[15px] font-medium">{link.to}</span>
                    </div>
                    <div class="text-[15px]">
                      <span class="font-semibold">Context:</span>
                      {link.context}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div class="border border-[#dfdaff] rounded-[10px] p-5">
              <div class="text-[20px] mb-4 font-semibold">Semantic Relationships</div>
              <div class="grid grid-cols-2 gap-4">
                {cluster.semantic_relationships.map((rel, i) => (
                  <div key={i} class=" p-4 rounded-[8px] border border-[#e5e7eb]">
                    <div class="text-[16px] font-semibold mb-3">{rel.term}</div>
                    <div class="flex gap-2 flex-wrap">
                      {rel.related_concepts.map((concept, idx) => (
                        <span key={idx} class="bg-[#6248fe1a] font-semibold text-[11px] text-[#6248FE] py-1 px-2 border border-[#6248fe61] rounded-[4px]">
                          {concept}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </FreeToolsLayout>
  );
};
export default TopicalMapGenerator;
